import React, { useCallback, useContext, useEffect, useState } from 'react';
import { ListCardBone, SkeletonLoader, useModal } from '@europrocurement/flexy-components';
import {
    BILLING_TYPE_VD,
    BILLING_TYPE_VI_L2D,
    BILLING_TYPE_VI_PRESCRIBER,
    BillingContext,
    DocumentPreviewContext,
    DossierFacturation,
    DossierPrescriberContext,
    InvoiceItemProps,
    InvoiceList,
    InvoiceStatus,
    ModalPaiement,
    NoResult,
    SelectedDossierContext,
    useBilling,
    useDossier,
    useDossierCollection,
    useDossierPrescripteur,
    useDownload,
    useExportDossier,
    useModalMail,
    usePanierPay,
    useToaster,
} from '@europrocurement/l2d-modules';
import {
    FactureFormaliteApiObject,
    useFacturationService,
    useSociete,
} from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import { AxiosError } from 'axios';
import useDossierPrefacturation, {
    DossiersArticlesReductions,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useDossierPrefacturation';
import useCollectionProvision, {
    CollectionProvisionType,
} from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useCollectionProvision';
import { CollectionDeDossierJsonldCollectiondossierRead } from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import useCollectionReglement from '@europrocurement/l2d-modules/modules/Dossiers/hooks/useCollectionReglement';
import {
    AjustementDePrestationFormalitJsonldAjustementCreate,
    ArticleTarifDTOJsonldDonneesFacturationRead,
    DossierTarifDTOJsonldDonneesFacturationRead,
} from '@europrocurement/l2d-domain/openApi/ApiFacturation';
import { UpdateDossierPayload } from '@europrocurement/l2d-modules/modules/Dossiers/types/UpdateDossierPayload';
import BillingAddressSection from './components/BillingAddressSection/BillingAddressSection';
import QuoteAndBillsSection from './components/QuoteAndBillsSection/QuoteAndBillsSection';

const FacturationTab: React.FunctionComponent = function () {
    const { dossier, updateDossier, siblings } = useContext(SelectedDossierContext);

    const { setDocumentPreview } = useContext(DocumentPreviewContext);

    const clientId = dossier?.clientId as number;
    const defaultBillingType = dossier?.typeFacturation as number;
    const clientOpereId = dossier?.clientOpereId as number;
    const prescriberId = dossier?.prescripteurId as number;
    const { getBillingAddress } = useDossierPrescripteur();
    const [recipientId, setRecipientId] = useState<number>(clientId);

    const [dataPrefacturation, setDataPrefacturation] = useState<DossiersArticlesReductions | null>(
        null,
    );
    const [acomptes, setAcomptes] = useState<Array<CollectionProvisionType>>([]);
    const [reglements, setReglements] = useState<Array<CollectionProvisionType>>([]);
    const [collection, setCollection] = useState<CollectionDeDossierJsonldCollectiondossierRead>();
    const [totalRemainingAmount, setTotalRemainingAmount] = useState<number>(0);
    const [invoiceToPayItems, setInvoiceToPayItems] = useState<Array<number>>([]);
    const { getInvoiceRemainingAmount } = useBilling({});
    const { searchDossierArticlesEtReductions, getDossiersFacturables } =
        useDossierPrefacturation();
    const { ajustementDePrestationFormalitApi } = useFacturationService();
    const { updateDossierArticles } = useDossier();
    const { searchCollectionProvision } = useCollectionProvision();
    const { searchCollectionReglement } = useCollectionReglement();

    const { handleApiResponse } = useToaster();
    const { modalActions } = useModal();
    const { getDossierCollection } = useDossierCollection();
    const xIdSociete = useSociete();

    const [actif, setActif] = useState<boolean | undefined>(true);
    const origine = 'B2D';
    const { estimates, invoices, estimatesLoaded, invoicesLoaded, setReload, updateActif } =
        useContext(BillingContext);
    const [invoiceItems, setInvoiceItems] = useState<Array<InvoiceItemProps>>([]);
    const { prescriber, sousClients, prescriberLoaded, setPrescriberId } =
        useContext(DossierPrescriberContext);

    const [dossiersIds, setDossiersIds] = React.useState<Array<number>>([]);
    const [dossiersSelected, setDossiersSelected] = React.useState<Array<number>>([]);
    const [actualDossiersSelected, setActualDossiersSelected] = React.useState<Array<number>>([]);

    const [onload, setOnload] = React.useState<boolean>(false);

    const clientAddresses = sousClients.map((client) => ({
        clientId: client.id as number,
        label: client.raisonSociale,
        siren: client.siren as string,
        address: getBillingAddress(client),
    }));
    const { enqueueSnackbar } = useSnackbar();
    const {
        getEstimateLabel,
        getInvoiceLabel,
        isInvoicePaid,
        downloadInvoice,
        downloadEstimate,
        canUpdateBillingInfos,
        validateInvoice,
        deleteInvoice,
        avoirInvoice,
    } = useBilling({ origine });
    const [billingType, setBillingType] = useState<number>(defaultBillingType);
    const { download } = useDownload();

    const handleRecipientChange = (newRecipientId?: number) => {
        setRecipientId(
            newRecipientId ||
                (prescriberId !== clientOpereId ? clientOpereId : (sousClients[0].id as number)),
        );

        // Add notification to user.
        enqueueSnackbar('Le client facturé a bien été modifié.', {
            variant: 'success',
        });
    };

    const handleBillingTypeChange = (newBillingType: number) => {
        setBillingType(newBillingType);

        // Add notification to user.
        enqueueSnackbar('Le choix du recouvrement a bien été modifié.', {
            variant: 'success',
        });
    };

    const statusFromInvoice = (invoice: FactureFormaliteApiObject): InvoiceStatus => {
        if (invoice.factureEnPreparation) {
            return 'draft';
        }
        if (isInvoicePaid(invoice)) {
            return 'paid';
        }

        return 'pending';
    };

    const feedbackFromInvoice = (invoice: FactureFormaliteApiObject): string => {
        if (invoice.factureEnPreparation) {
            return 'En préparation';
        }
        if (isInvoicePaid(invoice)) {
            return 'Réglé';
        }

        return 'À régler';
    };

    useEffect(() => {
        updateActif(actif);
    }, [actif, updateActif]);

    useEffect(() => {
        const payload: UpdateDossierPayload = {
            clientId: undefined,
            typeFacturation: undefined,
        };

        if (clientId !== recipientId) {
            payload.clientId = recipientId;
        }

        if (prescriberId === recipientId) {
            payload.typeFacturation = BILLING_TYPE_VI_PRESCRIBER;
        } else {
            const sousClient = sousClients.find((t) => t.id === recipientId);

            if (sousClient) {
                payload.typeFacturation = sousClient.venteIndirecte
                    ? BILLING_TYPE_VI_L2D
                    : BILLING_TYPE_VD;
            }
        }

        const newBillingType = payload.typeFacturation || defaultBillingType;
        const newClientId = payload.clientId || clientId;
        const hasChanges = newBillingType !== billingType || newClientId !== clientId;

        if (!hasChanges) {
            return;
        }

        if (newBillingType !== billingType) {
            setBillingType(newBillingType);
        }

        updateDossier({
            clientId: payload.clientId || clientId,
            typeFacturation: newBillingType,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [recipientId]);

    const allDataLoaded = prescriberLoaded;

    useEffect(() => {
        if (dossier.prescripteurId) {
            setPrescriberId(dossier.prescripteurId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossier.prescripteurId]);

    const reloadInvoiceEstimates = () => {
        setReload(true);
    };

    const reloadDossierArticlesEtReductions = (ids: number[], idsSelected: number[]) => {
        setOnload(true);
        searchDossierArticlesEtReductions(ids, idsSelected)
            .then(setDataPrefacturation)
            .finally(() => {
                setOnload(false);
            })
            .catch(() => {
                setOnload(false);
            });
    };

    const reloadAllData = () => {
        reloadInvoiceEstimates();
        reloadDossierArticlesEtReductions(dossiersIds, dossiersSelected);
        searchCollectionProvision(dossier.collectionId as number).then(setAcomptes);
        searchCollectionReglement(dossier.collectionId as number).then(setReglements);
        getDossierCollection(dossier.collectionId as number).then(setCollection);
    };

    const handleDownloadInvoice = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadInvoice(id)
                .then((fileContent: Blob) => {
                    download(fileName, fileContent);

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleShowInvoice = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadInvoice(id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleDownloadEstimate = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadEstimate(id)
                .then((fileContent: Blob) => {
                    download(fileName, fileContent);

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleShowEstimate = (fileName: string, id: number): Promise<void> =>
        new Promise((resolve, reject) => {
            downloadEstimate(id)
                .then((fileContent: Blob) => {
                    setDocumentPreview({
                        fileName,
                        fileType: 'pdf',
                        content: fileContent,
                    });

                    resolve(undefined);
                })
                .catch(reject);
        });

    const handleError = (error: AxiosError | Error) => {
        console.log(error);
        if (
            error instanceof AxiosError &&
            error.response &&
            error.response.data.exception &&
            error.response.data.exception.message
        ) {
            enqueueSnackbar(error.response.data.exception.message, {
                variant: 'error',
            });
        } else {
            enqueueSnackbar(
                error instanceof Error
                    ? error.message
                    : 'Une erreur est survenue lors de la validation de la facture',
                {
                    variant: 'error',
                    autoHideDuration: 2000,
                },
            );
        }
    };
    const handleValidateInvoice = (invoice: FactureFormaliteApiObject): Promise<void> =>
        new Promise(() => {
            if (!invoice.id) {
                throw new Error('Invoice ID is required');
            }
            validateInvoice(invoice.id)
                .then(() => {
                    enqueueSnackbar('Facture validée', {
                        autoHideDuration: 2000,
                    });
                    reloadAllData();
                })
                .catch((error) => {
                    handleError(error);
                });
        });

    const handleDeleteInvoice = (invoice: FactureFormaliteApiObject): Promise<void> =>
        new Promise(() => {
            if (!invoice.id) {
                throw new Error('Invoice ID is required');
            }
            if (invoice.factureEnPreparation) {
                deleteInvoice(invoice.id)
                    .then(() => {
                        enqueueSnackbar('Facture supprimée', {
                            autoHideDuration: 2000,
                        });
                        reloadAllData();
                    })
                    .catch((error) => {
                        handleError(error);
                    });
            } else {
                avoirInvoice(invoice.id)
                    .then(() => {
                        enqueueSnackbar('Avoir créé', {
                            autoHideDuration: 2000,
                        });
                        reloadAllData();
                    })
                    .catch((error) => {
                        handleError(error);
                    });
            }
        });

    useEffect(() => {
        setInvoiceToPayItems([
            ...invoices
                .filter((item) => item.paye === false && item.factureEnPreparation === false)
                .map((estimate) => estimate.id as number),
        ]);
        setInvoiceItems([
            ...estimates.map((estimate) => ({
                label: getEstimateLabel(estimate),
                status: 'paid' as InvoiceStatus,
                id: estimate.id as number,
                onDownload: handleDownloadEstimate,
                onShow: handleShowEstimate,
            })),
            ...invoices.map((invoice) => ({
                label: getInvoiceLabel(invoice),
                status: statusFromInvoice(invoice),
                validateInvoice: invoice.factureEnPreparation
                    ? () => {
                          handleValidateInvoice(invoice);
                      }
                    : null,
                deleteInvoice:
                    invoice.id !== undefined &&
                    invoice.idFactureRefAvoir === null &&
                    invoice.totalTtc !== null &&
                    invoice.totalTtc !== undefined &&
                    Number(invoice.totalTtc) > 0
                        ? () => {
                              handleDeleteInvoice(invoice);
                          }
                        : null,
                feedback: feedbackFromInvoice(invoice),
                id: invoice.id as number,
                onDownload: handleDownloadInvoice,
                onShow: handleShowInvoice,
            })),
        ]);
        setTotalRemainingAmount(
            invoices
                .filter((item) => item.paye === false && item.factureEnPreparation === false)
                .reduce((prev, current) => prev + getInvoiceRemainingAmount(current), 0),
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [estimates, invoices]);

    let devisFactures;

    if (!estimatesLoaded || !invoicesLoaded) {
        devisFactures = undefined;
    } else if (invoiceItems.length > 0) {
        devisFactures = <InvoiceList items={invoiceItems} />;
    } else {
        devisFactures = (
            <NoResult
                title="Aucun devis ou facture pour le moment"
                paddingSx="15px"
            />
        );
    }

    useEffect(() => {
        if (siblings && siblings[0]) {
            const ids = siblings.map((dossierPrefacturation) => {
                if (!dossierPrefacturation.collectionId) {
                    throw new Error('dossier sans colection');
                }
                if (!dossierPrefacturation.id) {
                    throw new Error('dossier sans id');
                }
                return dossierPrefacturation.id;
            });
            setDossiersIds(ids);
            getDossiersFacturables(ids)
                .then((fetchedSelected) => {
                    setDossiersSelected(fetchedSelected);
                })
                .catch((error) => {
                    console.error(
                        'Erreur lors du chargement des dossiers facturables / prefacturation',
                        error,
                    );
                });
            searchCollectionProvision(siblings[0].collectionId as number).then(setAcomptes);
            searchCollectionReglement(siblings[0].collectionId as number).then(setReglements);
            getDossierCollection(siblings[0].collectionId as number).then(setCollection);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siblings]);

    useEffect(() => {
        if (actualDossiersSelected !== dossiersSelected) {
            setActualDossiersSelected(dossiersSelected);

            reloadDossierArticlesEtReductions(dossiersIds, dossiersSelected);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dossiersSelected]);

    const afterSubmit = () => {
        modalActions.close();
        if (siblings) {
            reloadDossierArticlesEtReductions(dossiersIds, dossiersSelected);
        }
    };

    const afterSubmitAcompte = () => {
        modalActions.close();
        searchCollectionProvision(dossier.collectionId as number).then(setAcomptes);
    };
    const afterSubmitInvoice = () => {
        searchCollectionProvision(dossier.collectionId as number).then(setAcomptes);
        searchCollectionReglement(dossier.collectionId as number).then(setReglements);
    };
    const afterSubmitAcompteAttendu = () => {
        modalActions.close();
        getDossierCollection(dossier.collectionId as number).then(setCollection);
    };
    const sortableListOnChange = (
        items: (Omit<ArticleTarifDTOJsonldDonneesFacturationRead, 'id'> & {
            id: number;
        })[],
        articleDossier: DossierTarifDTOJsonldDonneesFacturationRead,
        dossierIdx: number,
    ) => {
        if (!dataPrefacturation) {
            throw new Error('dataPrefacturation is null');
        }
        // changer le rang de tous les items
        const newItems = items.map((item, index) => ({
            ...item,
            rang: index * 10 + 10,
        }));
        // constuire un nouveau dossier
        const newDossiers = [...(dataPrefacturation.dossiers || [])];

        if (!dataPrefacturation.dossiers || !dataPrefacturation.dossiers[dossierIdx]) {
            throw new Error('Prefacturation sans dossiers');
        }

        newDossiers.splice(dossierIdx, 1, {
            ...dataPrefacturation.dossiers[dossierIdx],
            articles: newItems,
        });

        setDataPrefacturation({
            ...dataPrefacturation,
            dossiers: newDossiers,
        });

        if (!articleDossier.id) {
            throw new Error('dossier sans id');
        }
        if (
            newDossiers[dossierIdx].articles === undefined ||
            newDossiers[dossierIdx].articles.length === 0
        ) {
            throw new Error('dossier sans articles');
        }

        const articlesUpdateDossierArticles = newDossiers[dossierIdx].articles.map((article) => ({
            id: article.id,
            rang: article.rang,
            libelle: article.libelle !== undefined ? article.libelle : null,
        }));

        updateDossierArticles(articleDossier.id, articlesUpdateDossierArticles);
    };

    const ajustInvoice = async () => {
        const articles =
            dataPrefacturation?.dossiers &&
            dataPrefacturation.dossiers.flatMap(
                (prefacturationdossier) =>
                    prefacturationdossier.articles?.map((article) => article.id as number) || [],
            );

        const acompteIds: number[] = acomptes.flatMap((acompte) =>
            (acompte.detailsReglements ?? []).map((detail) => {
                if (detail.id === undefined || detail.id === null) {
                    throw new Error('detail sans id');
                }
                return detail.id;
            }),
        );

        const ajustementDePrestationFormalitJsonldAjustementCreate: AjustementDePrestationFormalitJsonldAjustementCreate =
            {
                articles,
                acomptes: acompteIds,
            };
        const ajustementRequest =
            ajustementDePrestationFormalitApi.apiAjustementArticlesPrestationPost({
                xIdSociete,
                ajustementDePrestationFormalitJsonldAjustementCreate,
            });
        await handleApiResponse(ajustementRequest, afterSubmit);
    };

    const { bodyMailCollection, bodyMailFacture } = usePanierPay();
    const { showMailModal } = useModalMail();
    const { searchContacts } = useExportDossier();

    const onClickDemandeReglement = useCallback(
        async (type: string) => {
            try {
                const contact = await searchContacts('', [recipientId]);
                if (!contact || contact.length === 0) {
                    enqueueSnackbar('Aucun contact trouvé pour le client sélectionné', {
                        variant: 'error',
                    });
                    return;
                }
                if (type === 'facture' && collection !== undefined) {
                    const builtAttachments = await Promise.all(
                        invoiceToPayItems.map(async (id) => {
                            const blob = await downloadInvoice(id);
                            const invoice = invoices.find((inv) => inv.id === id);
                            const filename = invoice
                                ? `${getInvoiceLabel(invoice)}.pdf`
                                : `facture_${id}.pdf`;
                            return {
                                file: new File([blob], filename, { type: 'application/pdf' }),
                            };
                        }),
                    );

                    const body: string = await bodyMailFacture(
                        `${contact[0].firstName} ${contact[0].lastName}`,
                        collection,
                        dataPrefacturation?.resteAPayer || '0',
                        invoiceToPayItems,
                    );
                    showMailModal({
                        to: [
                            {
                                email: contact[0].emailAddress,
                                firstName: contact[0].firstName,
                                lastName: contact[0].lastName,
                            },
                        ],
                        subject: `Demande de règlement`,
                        body,
                        prescriber,
                        showBody: true,
                        showAttachments: true,
                        attachments: [...builtAttachments],
                    });
                } else if (type === 'collection' && collection !== undefined && collection.id) {
                    const body: string = await bodyMailCollection(
                        `${contact[0].firstName} ${contact[0].lastName}`,
                        collection,
                    );

                    showMailModal({
                        to: [
                            {
                                email: contact[0].emailAddress,
                                firstName: contact[0].firstName,
                                lastName: contact[0].lastName,
                            },
                        ],
                        subject: `Demande de provision`,
                        body,
                        prescriber,
                        showBody: true,
                        showAttachments: true,
                    });
                } else {
                    console.log('Pas de donnée à mettre dans le panier');
                    enqueueSnackbar('Une erreur est survenue', {
                        variant: 'error',
                    });
                }
            } catch (error) {
                console.log(error);
                enqueueSnackbar('Une erreur est survenue', {
                    variant: 'error',
                });
            }
        },
        [
            searchContacts,
            recipientId,
            collection,
            enqueueSnackbar,
            invoiceToPayItems,
            bodyMailFacture,
            dataPrefacturation?.resteAPayer,
            showMailModal,
            prescriber,
            downloadInvoice,
            invoices,
            getInvoiceLabel,
            bodyMailCollection,
        ],
    );

    const onClickPaiement = useCallback(
        (amount: number, ids: Array<number>, type: 'collection' | 'facture') => {
            modalActions.call(
                <ModalPaiement
                    ids={ids}
                    amount={amount}
                    type={type}
                    dossierNumero={dossier?.numero as string}
                    chequePayActif={false}
                    virementPayActif={false}
                    typoBackground={false}
                />,
            );
        },
        [dossier?.numero, modalActions],
    );

    const handleHideInactivesQuotes = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
            setActif(checked);
        },
        [],
    );

    const isLoading = !(allDataLoaded && prescriber && collection);

    return (
        <SkeletonLoader
            isLoading={isLoading}
            skeleton={
                <ListCardBone
                    itemCount={3}
                    contentHeight="220px"
                    gap="16px"
                />
            }
        >
            {prescriber ? (
                <BillingAddressSection
                    prescriberId={prescriberId}
                    recipientId={recipientId}
                    prescriberLabel={dossier.prescripteurLibelle || ' - '}
                    prescriberSiren={dossier.prescripteurSiren || ' - '}
                    prescriberAddress={getBillingAddress(prescriber)}
                    handleRecipientChange={handleRecipientChange}
                    clientAddresses={clientAddresses}
                    billingType={billingType}
                    handleBillingTypeChange={handleBillingTypeChange}
                    canEdit={canUpdateBillingInfos(invoices)}
                    isLoading={!dossier}
                />
            ) : null}

            <QuoteAndBillsSection
                actif={actif}
                handleHideInactivesQuotes={handleHideInactivesQuotes}
                devisFactures={devisFactures}
                invoices={invoices}
                totalRemainingAmount={totalRemainingAmount}
                invoiceToPayItems={invoiceToPayItems}
                onClickDemandeReglement={onClickDemandeReglement}
                onClickPaiement={onClickPaiement}
            />

            {collection ? (
                <DossierFacturation
                    reloadInvoiceEstimates={reloadInvoiceEstimates}
                    dataPrefacturation={dataPrefacturation}
                    acomptes={acomptes}
                    reglements={reglements}
                    collection={collection}
                    sortableListOnChange={sortableListOnChange}
                    ajustInvoice={ajustInvoice}
                    afterSubmit={afterSubmit}
                    afterSubmitAcompte={afterSubmitAcompte}
                    afterSubmitInvoice={afterSubmitInvoice}
                    afterSubmitAcompteAttendu={afterSubmitAcompteAttendu}
                    onClickDemandeReglement={onClickDemandeReglement}
                    onClickPaiement={onClickPaiement}
                    handleValidateInvoice={handleValidateInvoice}
                    onload={onload}
                    dossiersSelected={dossiersSelected}
                    onChangeDossiersSelected={(id, checked) => {
                        if (checked) {
                            setDossiersSelected((prev) => [...prev, id]);
                        } else {
                            setDossiersSelected((prev) => prev.filter((i) => i !== id));
                        }
                    }}
                />
            ) : null}
        </SkeletonLoader>
    );
};

export default FacturationTab;
